"use client";
import React, { Fragment } from "react";
import Image, { StaticImageData } from "next/image";
import { Box, Center, Grid, GridItem, HStack, Stack } from "@chakra-ui/react";
import Tag from "../tag";
import { useHeadingVariant, useTextVariant } from "@/theme/hooks/useSectionStyle";
import { Heading, Text } from "@/elements";
import TickImage from "public/shared/check-box.png";

type IntroProps = {
  children?: React.ReactNode;
  description?: React.ReactNode;
  conclusion?: string;
  heading?: string;
  image: StaticImageData;
  title?: string;
  points?: string[];
  imageHeight?: string;
};

const Intro = (props: IntroProps) => {
  const {
    children,
    description,
    heading,
    image,
    points,
    title,
    conclusion,
    imageHeight = "400px",
  } = props;
  const HeadingVariant = useHeadingVariant();
  const TextVariant = useTextVariant();
  return (
    <Grid
      gap={4}
      templateColumns={{
        base: "repeat(1, 1fr)",
        md: "repeat(5, 1fr)",
      }}
    >
      <GridItem colSpan={{ base: 1, md: 3 }}>
        <Stack gap={4}>
          {heading && <Tag label={heading} />}
          {title && (
            <Heading as="h2" variant={HeadingVariant} marginTop="10px">
              {title}
            </Heading>
          )}
          {description && (
            <Fragment>
              {typeof description === "string" ? (
                <Text
                  variant={TextVariant}
                  color="text.100"
                  dangerouslySetInnerHTML={{ __html: description }}
                />
              ) : Array.isArray(description) ? (
                <Stack gap={2}>
                  {description.map((desc, index) => (
                    <Text key={index} variant={TextVariant} color="text.100">
                      {desc}
                    </Text>
                  ))}
                </Stack>
              ) : (
                description
              )}
            </Fragment>
          )}
          {points && (
            <Stack color="text.100">
              {points.map((point, index) => (
                <HStack key={index} align="start">
                  <Image
                    alt={"Point"}
                    src={TickImage}
                    style={{ height: "auto", width: "20px", paddingTop: "4px" }}
                  />{" "}
                  <Text variant={TextVariant} fontWeight={550}>
                    {point}
                  </Text>
                </HStack>
              ))}
            </Stack>
          )}
          {children && children}
          {conclusion && (
            <Text variant={TextVariant} color="text.100" fontWeight={600}>
              {conclusion}
            </Text>
          )}
        </Stack>
      </GridItem>
      <GridItem colSpan={{ base: 1, md: 2 }}>
        <Center height={"full"}>
          <Box
            h={{ base: "250px", sm: imageHeight }}
            w={{ base: "100%", sm: "80%", md: "500px" }}
            position="relative"
            alignSelf={"center"}
          >
            <Image
              src={image}
              alt="IntroImg"
              fill
              style={{ objectFit: "contain", objectPosition: "center" }}
              placeholder="blur"
              loading="lazy"
            />
          </Box>
        </Center>
      </GridItem>
    </Grid>
  );
};

export default Intro;
